import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';

import {
  usePandaDocDocumentsQuery,
  PandaDocDocumentsQuery,
} from 'core/graphql';
import { usePandadocSync } from 'core/hooks';
import { LoadingIndicator } from 'atoms';
import { PATHS } from 'router/config';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from '../parts/groupColors';

interface IPandaDocTabProps {
  groupId: string;
}

const DocLink = styled.a`
  color: ${GroupColors.accent};
  font-weight: 600;
  text-decoration: none;
`;

const DocTypeHint = styled.div`
  font-size: 11px;
  color: ${GroupColors.textMuted};
`;

const CompletedCell = styled.td`
  color: ${GroupColors.textSecondary};
`;

type IDocRow = NonNullable<
  NonNullable<
    NonNullable<PandaDocDocumentsQuery['pandadocDocuments']>['edges'][number]
  >['node']
>;

const outcomeTone = (
  outcome: string | null | undefined,
  status: string | null | undefined,
): 'ok' | 'warn' | 'danger' | 'neutral' => {
  const o = (outcome || '').toLowerCase();
  const s = (status || '').toLowerCase();
  if (o === 'signed' || s.includes('completed')) return 'ok';
  if (o === 'lost' || s.includes('cancel')) return 'danger';
  if (s.includes('sent') || s.includes('viewed')) return 'warn';
  return 'neutral';
};

/**
 * PandaDocTab — single server-side filter `pandadocDocuments(groupId)`
 * (backend TZ §G1). Lists every document for any agreement of the
 * group, plus the global «Sync PandaDoc» button (`usePandadocSync` —
 * shared with StudentDetail). Click on a name opens the PandaDoc
 * document in a new tab; click on a student name routes to that
 * student's detail page.
 */
export const PandaDocTab = memo(({ groupId }: IPandaDocTabProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { data, loading, refetch } = usePandaDocDocumentsQuery({
    variables: { groupId, first: 100 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { triggerSync, syncing } = usePandadocSync(() => refetch());

  const docs: IDocRow[] = useMemo(
    () =>
      (data?.pandadocDocuments?.edges || [])
        .map((e) => e?.node)
        .filter((n): n is IDocRow => Boolean(n)),
    [data],
  );

  return (
    <S.DimWhenFetching fetching={loading}>
      <S.Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <S.CardTitle style={{ margin: 0 }}>
            {t('pages.groupDetail.pandadoc.title')}
          </S.CardTitle>
          <L.HeaderActions>
            <L.PrimaryActionButton
              type="button"
              tone="primary"
              disabled={syncing}
              onClick={triggerSync}
            >
              {syncing
                ? t('pages.groupDetail.pandadoc.syncing')
                : t('pages.groupDetail.pandadoc.sync')}
            </L.PrimaryActionButton>
          </L.HeaderActions>
        </div>

        {docs.length === 0 ? (
          <S.SubTitle style={{ marginTop: 8 }}>
            {loading
              ? t('pages.groupDetail.pandadoc.loading')
              : t('pages.groupDetail.pandadoc.empty')}
          </S.SubTitle>
        ) : (
          <div style={{ marginTop: 10 }}>
            <L.SmallTable>
              <thead>
                <tr>
                  <th>{t('pages.groupDetail.pandadoc.col.document')}</th>
                  <th>{t('pages.groupDetail.pandadoc.col.student')}</th>
                  <th>{t('pages.groupDetail.pandadoc.col.outcome')}</th>
                  <th>{t('pages.groupDetail.pandadoc.col.completed')}</th>
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => {
                  const student = doc.agreement?.student;
                  const u = student?.user;
                  const fullName = u
                    ? [u.firstName, u.lastName].filter(Boolean).join(' ')
                    : '';
                  const tone = outcomeTone(doc.outcome, doc.status);
                  return (
                    <tr key={doc.id}>
                      <td>
                        {doc.pandadocId ? (
                          <DocLink
                            href={`https://app.pandadoc.com/a/#/documents/${doc.pandadocId}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {doc.name || '—'}
                          </DocLink>
                        ) : (
                          doc.name || '—'
                        )}
                        <DocTypeHint>
                          {doc.docType || ''}{' '}
                          {doc.course ? `· ${doc.course}` : ''}
                        </DocTypeHint>
                      </td>
                      <td>
                        {student ? (
                          <L.ChipLink
                            type="button"
                            onClick={() =>
                              history.push(
                                PATHS.studentDetail.replace(':id', student.id),
                              )
                            }
                          >
                            {fullName || u?.email || '—'}
                          </L.ChipLink>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <L.Tag tone={tone}>
                          {doc.outcome || doc.status || '—'}
                        </L.Tag>
                      </td>
                      <CompletedCell>
                        {doc.dateCompleted
                          ? moment(doc.dateCompleted).format('DD.MM.YYYY')
                          : '—'}
                      </CompletedCell>
                    </tr>
                  );
                })}
              </tbody>
            </L.SmallTable>
          </div>
        )}
      </S.Card>

      {loading && docs.length === 0 ? (
        <S.CenterLoader>
          <LoadingIndicator />
        </S.CenterLoader>
      ) : null}
    </S.DimWhenFetching>
  );
});
